import React, { useEffect, useState } from 'react';
import { Worlds, errorMessage } from '../../api/client';
import { World } from '../../types';
import { Note } from '../ui/primitives';
import { REGIONS, getRegionFriendlyName } from '../../utils/regionUtils';

interface Props {
  open: boolean;
  world: World;
  onClose: () => void;
  /** Called with the new region code once the API has accepted it. */
  onChanged: (region: string) => void;
}

/** Pick a new hosting region → PUT /api/worlds/:id/region. The backend refuses while the world is running. */
const RegionDialog: React.FC<Props> = ({ open, world, onClose, onChanged }) => {
  const current = world.region || '';
  const [selected, setSelected] = useState(current);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setSelected(current);
    setBusy(false);
    setError(null);
  }, [open, current]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && !busy && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onClose]);

  if (!open) return null;
  const changed = selected !== '' && selected !== current;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!changed || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await Worlds.changeRegion(world.id, selected);
      onChanged(res.region || selected);
      onClose();
    } catch (err) {
      setError(errorMessage(err, 'Failed to change the region. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-stage" onClick={() => !busy && onClose()}>
      <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submit} role="dialog" aria-modal="true" aria-label="Change region">
        <h3>Change region</h3>
        <p>
          Pick where <b>{world.name}</b> is hosted. Closer to you and your players means lower ping.
        </p>
        {error && <Note tone="danger">{error}</Note>}
        <div className="region-opts" role="radiogroup" aria-label="Region">
          {REGIONS.map((r) => {
            const isCurrent = r.code === current;
            return (
              <button
                key={r.code}
                type="button"
                className="region-opt"
                role="radio"
                aria-checked={selected === r.code}
                onClick={() => setSelected(r.code)}
                disabled={busy}
              >
                <span>
                  <b>{r.name}</b>
                  <small>{r.hint}</small>
                </span>
                {isCurrent && <span className="chip">current</span>}
              </button>
            );
          })}
        </div>
        <Note tone="warn">
          The move only takes effect the next time the world starts. Nothing changes until then, and the first start in a new
          region can take a little longer while the world is copied over.
        </Note>
        <div className="modal-actions">
          <button type="button" className="btn btn--ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="btn btn--primary" disabled={busy || !changed}>
            {busy && <span className="spin" style={{ borderTopColor: '#fff' }} />}
            {busy ? 'Changing…' : changed ? `Move to ${getRegionFriendlyName(selected)}` : 'Change region'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default RegionDialog;
